/**
 * CAND-DISP-1 — Candidate Review panel.
 *
 * Pending candidates only. Per candidate the reviewer sees the retained
 * display name and identity state, then chooses exactly one of Link, Promote
 * or Reject, and confirms. Deliberately not a candidate-management surface:
 * no bulk actions, no matching controls, no queue administration.
 */

import { useState } from "react";
import { useCandidateReview } from "../hooks";
import { firstOf, type Disposition, type PendingCandidate } from "../types";

function CandidateRow({
  candidate,
  busy,
  matches,
  searching,
  onSearch,
  onConfirm,
}: {
  candidate: PendingCandidate;
  busy: boolean;
  matches: { organization_id: string; legal_name: string }[];
  searching: boolean;
  onSearch: (query: string) => void;
  onConfirm: (
    disposition: Disposition,
    canonicalOrganizationId: string | null,
    notes: string | null
  ) => void;
}) {
  const [choice, setChoice] = useState<Disposition | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [term, setTerm] = useState("");

  const org = firstOf(candidate.candidate_organizations);
  const source = firstOf(candidate.candidate_organization_sources);
  const canConfirm =
    choice === "promote" || choice === "reject" || (choice === "link" && selectedOrg !== "");

  return (
    <li className="candidate-review__row">
      <div className="candidate-review__identity">
        <strong>{org?.display_name ?? "(no retained display name)"}</strong>
        <span className="candidate-review__identity-state">
          Identity:{" "}
          {org?.authoritative_namespace
            ? `${org.authoritative_namespace}:${org.authoritative_id_raw ?? ""}`
            : "none on record"}
        </span>
        <span>Queued {new Date(candidate.queued_at).toLocaleString()}</span>
        <span className="candidate-review__event">
          Source event {source?.source_event_id ?? "unknown"}
        </span>
      </div>

      <div className="candidate-review__actions">
        <button type="button" disabled={busy} onClick={() => setChoice("link")}>
          Link to existing
        </button>
        <button type="button" disabled={busy} onClick={() => setChoice("promote")}>
          Promote as new
        </button>
        <button type="button" disabled={busy} onClick={() => setChoice("reject")}>
          Reject
        </button>
      </div>

      {choice === "link" && (
        <div className="candidate-review__link">
          <input
            type="search"
            value={term}
            placeholder="Search existing organizations by legal name"
            onChange={(event) => setTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") onSearch(term);
            }}
          />
          <button type="button" disabled={searching} onClick={() => onSearch(term)}>
            {searching ? "Searching…" : "Search"}
          </button>
          <select value={selectedOrg} onChange={(event) => setSelectedOrg(event.target.value)}>
            <option value="">Select an organization…</option>
            {matches.map((match) => (
              <option key={match.organization_id} value={match.organization_id}>
                {match.legal_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {choice === "promote" && (
        <p className="candidate-review__note">
          Creates a new canonical organization named{" "}
          <strong>{org?.display_name ?? "(none)"}</strong>, verbatim. No authoritative identifier is
          written.
        </p>
      )}

      {choice && (
        <div className="candidate-review__confirm">
          <input
            type="text"
            value={notes}
            placeholder="Review notes (optional)"
            onChange={(event) => setNotes(event.target.value)}
          />
          <button
            type="button"
            disabled={busy || !canConfirm}
            onClick={() =>
              onConfirm(
                choice,
                choice === "link" ? selectedOrg : null,
                notes.trim() ? notes.trim() : null
              )
            }
          >
            {busy ? "Working…" : `Confirm ${choice}`}
          </button>
        </div>
      )}
    </li>
  );
}

export function CandidateReview() {
  const {
    candidates,
    loading,
    busyId,
    error,
    result,
    matches,
    searching,
    refresh,
    search,
    dispose,
  } = useCandidateReview();

  return (
    <details className="settings-disclosure candidate-review">
      <summary className="settings-disclosure__summary">
        <span>
          <strong>Source identity review</strong>
          <small>Resolve source names that could not be matched to a known company.</small>
        </span>
        <span className="settings-disclosure__summary-meta">
          {!loading ? `${candidates.length} pending` : "Loading"}
          <span className="settings-disclosure__chevron" aria-hidden="true">
            ⌄
          </span>
        </span>
      </summary>

      <div className="settings-disclosure__body candidate-review__body">
        <div className="candidate-review__intro">
          <p>
            Use this only when a source organization has no reliable identity. Link it to an
            existing company, promote it as a new company, or reject it. This is separate from
            approving a signal for the user feed.
          </p>
          <button
            type="button"
            className="sx-btn sx-btn-p3"
            onClick={() => void refresh()}
            disabled={loading}
          >
            {loading ? "Loading…" : "Refresh list"}
          </button>
        </div>

        {error && (
          <p className="candidate-review__message" role="alert">
            {error}
          </p>
        )}

        {result && (
          <p className="candidate-review__message" role="status">
            {result.status === "already_resolved"
              ? "Already resolved by another reviewer — no action taken."
              : `Resolved (${result.disposition}). ${
                  result.redrive
                    ? `Re-driven under sweep ${result.sweep_run_id ?? "unknown"}.`
                    : "No re-drive for a rejected candidate."
                }`}
          </p>
        )}

        {/*
          The authoritative empty state renders only after a SUCCESSFUL empty
          response. On a failed list the error banner stands alone, because
          "no candidates pending" is a claim the panel has no evidence for when
          the read failed. Previously loaded rows stay visible beneath the error.
        */}
        {!loading && !error && candidates.length === 0 && (
          <p className="candidate-review__empty">No source identities need review.</p>
        )}

        <ul className="candidate-review__list">
          {candidates.map((candidate) => (
            <CandidateRow
              key={candidate.candidate_organization_id}
              candidate={candidate}
              busy={busyId === candidate.candidate_organization_id}
              matches={matches}
              searching={searching}
              onSearch={(query) => void search(query)}
              onConfirm={(disposition, canonicalOrganizationId, notes) =>
                void dispose(
                  candidate.candidate_organization_id,
                  disposition,
                  canonicalOrganizationId,
                  notes
                )
              }
            />
          ))}
        </ul>
      </div>
    </details>
  );
}
